import { BehaviorSubject, Subject } from 'rxjs';
import * as i0 from "@angular/core";
/**
 * This services records audio using MediaRecorder and outputs an audio file with MIME type `audio/webm;codecs=opus
 */
export class AudioRecordingService {
    constructor() {
        this.recordingEvent = new Subject();
        this.recordingStateSubject = new BehaviorSubject(false);
        this.durationSubject = new BehaviorSubject(0);
        // Audio data
        this.mediaRecorder = null;
        this.audioContext = null;
        this.analyser = null;
        this.microphone = null;
        this.stream = null;
        this.recordingChunks = [];
        // Constants
        this.RECORDING_START_TIME = 0;
        this.MAX_RECORDING_TIME = 120000; // 2 minutes in milliseconds
    }
    ngOnDestroy() {
        if (this.recordingStateSubject.value) {
            this.stop();
        }
        this.cleanup();
        this.recordingEvent.complete();
        this.recordingStateSubject.complete();
        this.durationSubject.complete();
    }
    // Observables
    getRecordingEvent() {
        return this.recordingEvent.asObservable();
    }
    // Controllers
    async start() {
        try {
            if (this.recordingStateSubject.value) {
                return;
            }
            // Request microphone access
            this.stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100,
                },
            });
            // Setup audio context for frequency analysis
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.analyser = this.audioContext.createAnalyser();
            this.microphone = this.audioContext.createMediaStreamSource(this.stream);
            this.analyser.fftSize = 2048;
            this.analyser.smoothingTimeConstant = 0.8;
            this.microphone.connect(this.analyser);
            // Setup MediaRecorder
            const options = {
                mimeType: 'audio/webm;codecs=opus',
                audioBitsPerSecond: 128000,
            };
            this.mediaRecorder = new MediaRecorder(this.stream, options);
            this.recordingChunks = [];
            this.mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    this.recordingChunks.push(event.data);
                }
            };
            this.mediaRecorder.onstop = () => {
                this.processRecording();
            };
            this.mediaRecorder.onerror = () => {
                this.recordingEvent.next({ event: 'ERROR', error: 'Recording error occurred' });
                this.cleanup();
            };
            // Start recording
            this.mediaRecorder.start(250); // Collect data every 250ms
            this.RECORDING_START_TIME = Date.now();
            this.recordingStateSubject.next(true);
            // Set up automatic stop after 2 minutes
            this.recordingTimeoutTimer = setTimeout(() => {
                if (this.recordingStateSubject.value) {
                    this.stop();
                }
            }, this.MAX_RECORDING_TIME);
            // Start volume analysis
            this.startVolumeAnalysis();
            // Start duration tracking
            this.startDurationTracking();
            this.recordingEvent.next({ event: 'RECORDING_STARTED' });
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to start recording';
            this.recordingEvent.next({ event: 'ERROR', error: errorMessage });
            this.cleanup();
        }
    }
    stop() {
        if (!this.recordingStateSubject.value || !this.mediaRecorder) {
            return;
        }
        this.recordingStateSubject.next(false);
        // Clear intervals and timeout
        this.clearTimers();
        // Stop frequency analysis
        this.stopVolumeAnalysis();
        // Stop recording
        this.mediaRecorder.stop();
        this.recordingEvent.next({ event: 'RECORDING_STOPPED' });
    }
    //
    startVolumeAnalysis() {
        if (!this.analyser)
            return;
        const bufferLength = this.analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        const updateVolume = () => {
            if (!this.recordingStateSubject.value || !this.analyser)
                return;
            this.analyser.getByteTimeDomainData(dataArray);
            let sum = 0;
            for (let i = 0; i < bufferLength; i += 1) {
                const value = (dataArray[i] - 128) / 128; // normalize to -1 to 1 range
                sum += value * value;
            }
            const rms = Math.sqrt(sum / bufferLength); // root mean square = volume
            // Amplify and clamp the volume to 0-1 range for better visualization
            const amplifiedVolume = Math.min(rms * 5, 1); // multiply by 5 to make quieter sounds visible
            this.recordingEvent.next({
                event: 'FREQUENCY_DATA',
                volume: amplifiedVolume,
            });
        };
        this.frequencyUpdateInterval = setInterval(updateVolume, 80);
    }
    stopVolumeAnalysis() {
        if (this.frequencyUpdateInterval) {
            clearInterval(this.frequencyUpdateInterval);
            this.frequencyUpdateInterval = null;
        }
    }
    startDurationTracking() {
        this.durationSubject.next(0);
        this.durationUpdateInterval = setInterval(() => {
            if (this.recordingStateSubject.value) {
                const duration = Date.now() - this.RECORDING_START_TIME;
                this.durationSubject.next(duration);
            }
        }, 100); // Update every 100ms
    }
    clearTimers() {
        if (this.recordingTimeoutTimer) {
            clearTimeout(this.recordingTimeoutTimer);
            this.recordingTimeoutTimer = null;
        }
        if (this.durationUpdateInterval) {
            clearInterval(this.durationUpdateInterval);
            this.durationUpdateInterval = null;
        }
    }
    processRecording() {
        if (this.recordingChunks.length === 0) {
            this.recordingEvent.next({ event: 'ERROR', error: 'No audio data recorded' });
            this.cleanup();
            return;
        }
        const mimeType = 'audio/webm;codecs=opus';
        const audioBlob = new Blob(this.recordingChunks, { type: mimeType });
        const audioUrl = URL.createObjectURL(audioBlob);
        const duration = Date.now() - this.RECORDING_START_TIME;
        const recordingData = {
            blob: audioBlob,
            duration,
            url: audioUrl,
        };
        this.recordingEvent.next({
            event: 'AUDIO_READY',
            audioData: recordingData,
        });
        this.cleanup();
    }
    cleanup() {
        // Clear all timers
        this.clearTimers();
        // Stop frequency analysis
        this.stopVolumeAnalysis();
        // Close audio context
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close();
        }
        // Stop media stream
        if (this.stream) {
            this.stream.getTracks().forEach((track) => track.stop());
        }
        // Reset properties
        this.mediaRecorder = null;
        this.audioContext = null;
        this.analyser = null;
        this.microphone = null;
        this.stream = null;
        this.recordingChunks = [];
        this.RECORDING_START_TIME = 0;
        // Reset state
        this.recordingStateSubject.next(false);
        this.durationSubject.next(0);
    }
}
AudioRecordingService.ɵfac = function AudioRecordingService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || AudioRecordingService)(); };
AudioRecordingService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AudioRecordingService, factory: AudioRecordingService.ɵfac, providedIn: 'root' });
